import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CopyIcon } from '@rsos/assets/icons';
import { BE_END_CALL } from '@rsos/constants/callStatuses';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { trackTranscriptionTabCopy } from '../../../../../../../gainsightTrackings/callTranscriptionTrackings';
import CopyButton from '../../TranscriptViewer/IconButton';

interface CopyKeywordsProps {
  keywords: string[];
}

const CopyKeywords = ({ keywords }: CopyKeywordsProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const callStateRef = useRef<string | null>(null);

  const callerID = useSelector(
    (state: StateWithEmergencyCalls) =>
      state.emergencyCalls.selectedCallerID || '',
  );
  const callDetails = useSelector(
    (state: StateWithEmergencyCalls) =>
      state.emergencyCalls.callQueue[callerID],
  );

  // Track the call state at the time the user selected the Transcription Tab.
  useEffect(() => {
    if (
      callStateRef.current === null &&
      callerID &&
      callDetails?.callStatuses
    ) {
      callStateRef.current =
        callDetails?.callStatuses?.at(-1) === BE_END_CALL ? 'ended' : 'active';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerID, callDetails?.callStatuses]);

  const handleClick = () => {
    if (!keywords?.length) {
      return;
    }

    if (!navigator.clipboard) {
      // eslint-disable-next-line no-console -- browser capability not available
      console.error(
        'Copy feature unavailable. It is only available on a secure (HTTPS) connection.',
      );
      return;
    }
    const keywordsText = keywords.join(', ');
    navigator.clipboard.writeText(keywordsText);

    setIsCopied(true);
    trackTranscriptionTabCopy({
      copyType: 'keywords',
      callState: callStateRef.current || 'unknown',
    });
    window.setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  const isDisabled = !keywords?.length;
  const tooltip = isDisabled
    ? 'Copy is available after keywords are detected.'
    : isCopied
      ? 'Copied'
      : 'Copy Keywords';

  return (
    <CopyButton
      dataName="keywords-copy-button"
      disabled={isDisabled}
      size="small"
      onClick={handleClick}
      tooltip={tooltip}
    >
      <CopyIcon height={12} width={12} />
    </CopyButton>
  );
};

export default CopyKeywords;
